import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, MagnifyingGlass, Storefront, UserPlus, UsersThree, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, mediaUrl } from "../lib/api";

function Avatar({ url, name }) {
  return url ? (
    <img src={mediaUrl(url)} alt="" className="h-10 w-10 rounded-full border border-neon-violet/40 object-cover" />
  ) : (
    <div className="flex h-10 w-10 items-center justify-center rounded-full border border-neon-gold/40 bg-neon-gold/10 font-display text-sm font-black text-neon-gold">
      {(name || "N")[0].toUpperCase()}
    </div>
  );
}

export default function SearchOverlay({ onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [requested, setRequested] = useState({});
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    const t = setTimeout(() => {
      API.get(`/search?q=${encodeURIComponent(q.trim())}`)
        .then((r) => setResults(r.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const addFriend = async (u) => {
    try {
      await API.post("/friends/request", { query: u.handle });
      setRequested((s) => ({ ...s, [u.id]: true }));
      toast.success(`Request sent to ${u.handle}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const go = (path) => { onClose(); navigate(path); };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black/85 backdrop-blur" data-testid="search-overlay"
      onClick={onClose}>
      <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
        className="mx-auto mt-20 w-full max-w-[430px] px-5" onClick={(e) => e.stopPropagation()}>
        <div className="rounded-3xl border border-white/10 bg-[#0a0a0a] p-4 shadow-2xl">
          <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-black px-4 py-3">
            <MagnifyingGlass size={18} className="shrink-0 text-neon-gold" />
            <input ref={inputRef} data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search people, promoters, venues…"
              className="w-full bg-transparent text-sm outline-none placeholder:text-zinc-600" />
            <button data-testid="search-close-btn" onClick={onClose} className="shrink-0 text-zinc-500 hover:text-white">
              <X size={16} />
            </button>
          </div>

          <div className="mt-3 max-h-[55vh] space-y-1 overflow-y-auto">
            {results && results.users.length === 0 && results.venues.length === 0 && (
              <p className="py-6 text-center text-sm text-zinc-500" data-testid="search-empty">No matches for "{q}"</p>
            )}
            {results?.users?.length > 0 && (
              <div className="px-2 pt-2 text-[10px] font-bold uppercase tracking-widest text-zinc-600">People</div>
            )}
            {results?.users?.map((u) => (
              <div key={u.id} data-testid={`search-user-${u.id}`}
                className="flex items-center justify-between gap-3 rounded-2xl px-2 py-2 transition-colors hover:bg-white/5">
                <button className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  onClick={() => u.role === "promoter" ? go(`/${u.handle}`) : null}>
                  <Avatar url={u.avatar_url} name={u.name} />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-bold">{u.name}</div>
                    <div className="truncate text-[11px] text-neon-violet">{u.handle}{u.title ? ` · ${u.title}` : ""}</div>
                  </div>
                </button>
                {u.role === "promoter" ? (
                  <button data-testid={`search-view-${u.id}`} onClick={() => go(`/${u.handle}`)}
                    className="shrink-0 rounded-full border border-neon-violet/40 px-3 py-1 text-[10px] font-bold text-neon-violet">
                    <UsersThree size={12} className="mr-1 inline" /> View
                  </button>
                ) : (
                  <button data-testid={`search-add-${u.id}`} onClick={() => addFriend(u)}
                    disabled={requested[u.id]}
                    className="shrink-0 rounded-full border border-neon-gold/40 px-3 py-1 text-[10px] font-bold text-neon-gold disabled:opacity-40">
                    <UserPlus size={12} className="mr-1 inline" /> {requested[u.id] ? "Sent" : "Add"}
                  </button>
                )}
              </div>
            ))}
            {results?.venues?.length > 0 && (
              <div className="px-2 pt-3 text-[10px] font-bold uppercase tracking-widest text-zinc-600">Venues</div>
            )}
            {results?.venues?.map((v) => (
              <button key={v.id} data-testid={`search-venue-${v.id}`} onClick={() => go(`/venue/${v.id}`)}
                className="flex w-full items-center gap-3 rounded-2xl px-2 py-2 text-left transition-colors hover:bg-white/5">
                {v.image ? (
                  <img src={mediaUrl(v.image)} alt="" className="h-10 w-10 rounded-xl object-cover" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5"><Storefront size={18} className="text-zinc-500" /></div>
                )}
                <div className="min-w-0">
                  <div className="truncate text-sm font-bold">{v.name}</div>
                  {v.address && <div className="flex items-center gap-1 truncate text-[11px] text-zinc-500"><MapPin size={10} /> {v.address}</div>}
                </div>
              </button>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
